// FT-102 (DEC-01, REQ-08): клиентская арифметика бронируемых допов.
// Вынесена из BookingAddonsPanel по конвенции unit-тестов (см. шапку
// bookingTotalPreview.spec.js): детерминированные формулы тестируются напрямую.

// FT-102 доработка: цена/залог СТРОКИ, если они пришли (могут быть 0 —
// поэтому проверка на null/undefined, а не на falsy), иначе каталожные.
// Старый API line_* не отдаёт → поведение прежнее (обратная совместимость).
export function effectivePrice(addon) {
  const line = addon && addon.line_price;
  return Number(line === null || line === undefined ? addon && addon.rental_price : line) || 0;
}

export function effectiveDeposit(addon) {
  const line = addon && addon.line_deposit;
  return Number(line === null || line === undefined ? addon && addon.deposit : line) || 0;
}

// Ревью Task 7 (Important): цена/залог, введённые ДО того как у позиции
// появилась строка (line_id), хранятся в pendingPricing на компоненте
// (id -> {price, deposit}) — единое хранилище для live (строки ещё нет) и
// draft (брони ещё нет). mergePendingPricing накладывает их поверх addon,
// чтобы effectivePrice/effectiveDeposit и суммы панели показывали именно
// то, что будет сохранено, а не каталожное значение.
//
// Ревью round 4 (п.4): сервер в draft-режиме всегда отдаёт
// price_overridden/deposit_overridden === false (строки ещё нет — сервер
// вообще не знает о договорной цене) — без локального сравнения с
// каталожным значением иконка/пометка ручной правки в новой брони не
// появляется вовсе, хотя механизм контроля для этого и придуман.
export function mergePendingPricing(addon, pending) {
  if (!pending) return addon;
  const merged = { ...addon };
  if (pending.price !== undefined && pending.price !== null) {
    merged.line_price = pending.price;
    merged.price_overridden = Number(pending.price) !== (Number(addon && addon.rental_price) || 0);
  }
  if (pending.deposit !== undefined && pending.deposit !== null) {
    merged.line_deposit = pending.deposit;
    merged.deposit_overridden = Number(pending.deposit) !== (Number(addon && addon.deposit) || 0);
  }
  return merged;
}

// Тело POST для создания строки — подмешивает отложенную цену/залог, иначе
// строка создаётся по каталожной цене, а введённая менеджером договорная
// теряется молча (находка ревью Task 7).
export function buildCreatePayload(addonId, quantity, pending) {
  const payload = { store_product_id: addonId, quantity };
  if (pending && pending.price !== undefined && pending.price !== null) payload.price = pending.price;
  if (pending && pending.deposit !== undefined && pending.deposit !== null) payload.deposit = pending.deposit;
  return payload;
}

// Санитизация ввода цены/залога — НЕ симметрично количеству буквально:
// пустое поле (очистили и кликнули мимо) — это НЕ 0, это «не менять».
// Серверный money_param (booking_store_products_controller.rb) трактует
// "" и nil как «параметр не передан» и не трогает сохранённое значение;
// ноль ставится ТОЛЬКО явным вводом 0. Раньше normalizeMoney("") давала 0,
// и applyPricing слал {price: 0} на пустое поле — молчаливое обнуление
// цены на сервере (ревью Task 7, round 3; в проекте уже были дорогие
// инциденты класса «деньгам доверили пустую строку»).
// Возвращает undefined для пустого ввода — вызывающий код обязан пропустить
// такой ключ, а не класть undefined в тело запроса. Мусор и отрицательное
// значение — это осмысленный ввод, коэрцируются в 0 («сделать бесплатным»).
export function normalizeMoney(rawValue) {
  if (rawValue === "" || rawValue === null || rawValue === undefined) return undefined;
  const num = Number(rawValue);
  if (!Number.isFinite(num) || num < 0) return 0;
  return Math.round(num * 100) / 100;
}

// Сумма допов: per_unit → price × qty × units (units — единица тарификации
// брони: дни/часы/недели/месяцы); per_booking → price × qty, срок не влияет.
export function addonsCost(addons, selection, units) {
  return (addons || []).reduce((sum, addon) => {
    const qty = Number(selection && selection[addon.id]) || 0;
    if (qty <= 0) return sum;
    const effectiveUnits = addon.pricing_mode === "per_booking" ? 1 : Math.max(1, Number(units) || 1);
    return Math.round((sum + effectivePrice(addon) * qty * effectiveUnits) * 100) / 100;
  }, 0);
}

// Суммарный залог допов (за штуку × qty)
export function addonsDeposit(addons, selection) {
  return (addons || []).reduce((sum, addon) => {
    const qty = Number(selection && selection[addon.id]) || 0;
    return Math.round((sum + effectiveDeposit(addon) * qty) * 100) / 100;
  }, 0);
}

// SC-09 (UI): префилл депозита дельтой залога допов — только пока менеджер
// не правил поле вручную; money-мусор ("" / null) коэрцится в 0.
export function prefillDeposit(currentDeposit, delta, touchedManually) {
  const current = Number(currentDeposit) || 0;
  if (touchedManually || !delta) return current;
  return Math.round((current + delta) * 100) / 100;
}

// Ревью PR-B (fresh): у hourly/weekly/monthly броней залог живёт в своих полях
// (ветвление как в currentDeposit обоих компонентов) — префилл REQ-08 обязан
// писать в то же поле, которое отображается и уходит на сервер, иначе дельта
// оседает в мёртвом booking.deposit и залог допов молча теряется.
export function depositFieldFor(booking) {
  if (booking && booking.hourly) return "hourly_deposit";
  if (booking && booking.weekly) return "weekly_deposit";
  if (booking && booking.monthly) return "monthly_deposit";
  return "deposit";
}

// Само значение залога по типу аренды. `|| 0` не косметика: отсутствующее поле
// даёт NaN, а NaN в money-сумме заражает итог целиком и роняет сравнения
// (`NaN > 0` ложно), из-за чего бронь молча выпадает из подсчёта.
export function bookingDeposit(booking) {
  return booking[depositFieldFor(booking)] || 0;
}
